import {
  buildFolderBookingList,
  FOLDER_ROWS_ALL,
  FolderRowTypeEnum,
} from './folderBookingRows';
import { getFolderMainGuest } from './customerFile';

const DAY_MS = 24 * 60 * 60 * 1000;

const daysBetween = (startAt, endAt) =>
  Math.round(
    (new Date(endAt).getTime() - new Date(startAt).getTime()) / DAY_MS,
  );

/**
 * KD-3842 client folder summary: the stay (first start, last end, nights and
 * hotel destinations) and the KPI figures. Built on buildFolderBookingList so
 * the KPIs and the bookings list always show the same numbers.
 */
export const buildFolderSummary = (folder) => {
  const list = buildFolderBookingList(folder);
  const services = [
    ...list.trips.flatMap((trip) => trip.items),
    ...list.standalone,
  ];
  // Cancelled, refused or replaced services do not set the stay dates.
  const dated = [
    ...list.trips,
    ...services.filter((service) => service.countsInTotals),
  ];

  const starts = dated
    .map((row) => row.startAt)
    .filter(Boolean)
    .sort();
  const ends = dated
    .map((row) => row.endAt)
    .filter(Boolean)
    .sort();
  const startAt = starts[0] ?? null;
  const endAt = ends[ends.length - 1] ?? null;

  // Destinations in stay order, with the hotel nights spent in each city.
  const destinations = [];
  services
    .filter(
      (service) =>
        service.type === FolderRowTypeEnum.HOTEL &&
        service.countsInTotals &&
        service.city,
    )
    .sort((a, b) => String(a.startAt).localeCompare(String(b.startAt)))
    .forEach((service) => {
      const known = destinations.find((d) => d.city === service.city);
      if (known) {
        known.nights += service.nights ?? 0;
      } else {
        destinations.push({ city: service.city, nights: service.nights ?? 0 });
      }
    });

  return {
    stay: {
      startAt,
      endAt,
      nights:
        startAt && endAt ? Math.max(daysBetween(startAt, endAt), 0) : null,
      destinations,
    },
    totals: list.totals,
    bookings: {
      count:
        list.counts[FolderRowTypeEnum.HOTEL] +
        list.counts[FolderRowTypeEnum.EXPERIENCE],
      trips: list.counts[FolderRowTypeEnum.TRIP],
      hotels: list.counts[FolderRowTypeEnum.HOTEL],
      experiences: list.counts[FolderRowTypeEnum.EXPERIENCE],
    },
    hasServices: list.counts[FOLDER_ROWS_ALL] > 0,
    travelersCount: (folder?.travelers ?? []).length,
    mainGuest: getFolderMainGuest(folder),
    // KD-3910: the latest change to the folder or to its bookings and trips.
    updatedAt: folder?.lastActivityAt ?? folder?.updatedAt ?? null,
  };
};
